import React from 'react';
import DatePicker, { DateObject } from 'react-multi-date-picker';

import { Alert, Switch, Tag } from 'antd';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { timeArrayToString, timeStringToArray } from '@libs/NwMoment';

import TimePicker from '@components/EventDateTimePicker/TimePicker';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    .dates-row {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 2rem;

        .date-picker-container {
            padding-top: 1rem;
            padding-left: 2rem;
            .add-date-icon {
                cursor: pointer;
                font-size: 2rem;
                transition: opacity 0.2s;

                &:hover {
                    opacity: 0.7;
                }
            }

            .rmdp-calendar {
                .rmdp-header {
                    .rmdp-arrow-container {
                        padding: 2px 0;
                        .rmdp-arrow {
                            border: solid ${(props) => props.theme.PrimaryColor};
                            border-width: 0 2px 2px 0;
                        }
                        &:hover {
                            background-color: ${(props) => props.theme.PrimaryColor};
                            .rmdp-arrow {
                                border: solid #fff;
                                border-width: 0 2px 2px 0;
                            }
                        }
                    }
                }
            }
            .rmdp-week {
                .rmdp-week-day {
                    color: ${(props) => props.theme.PrimaryColor};
                }
            }
            .rmdp-day {
                &.rmdp-selected {
                    span:not(.highlight) {
                        background-color: ${(props) => props.theme.PrimaryColor};
                        &:hover {
                            background-color: ${(props) => props.theme.PrimaryColor};
                        }
                    }
                }
                &.rmdp-today {
                    span {
                        background-color: transparent;
                        font-weight: bold;
                        color: ${(props) => props.theme.TextColor};
                    }
                }
                &:not(.rmdp-disabled, .rmdp-day-hidden) {
                    span {
                        &:hover {
                            background-color: ${(props) => props.theme.PrimaryColor};
                        }
                    }
                }
            }
        }

        .selected-dates-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            align-items: center;
            padding-right: 1rem;

            .ant-tag {
                margin: 0;
                display: flex;
                align-items: center;
            }

            .clear-all-btn {
                cursor: pointer;
                color: ${(props) => props.theme.DangerColor};
                font-size: ${(props) => props.theme.FontSizeSm};
            }
        }
    }

    .time-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 1rem;
        flex-wrap: wrap;
        padding: 1.25rem 2rem;

        p {
            width: 3.6rem;
            font-size: 12px;
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
            margin: 0;
        }

        .all-day-container {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
        }
    }
`;

const MultipleDateSelector = ({ value, onChange }) => {
    if (!value) {
        return null;
    }

    const { dates, startTime, endTime, allDay } = value;

    const handleDatesChange = (dateObjects) => {
        const newDates = dateObjects.map((d) => d.toDate());
        onChange({
            ...value,
            dates: newDates,
        });
    };

    const handleRemoveDate = (index) => {
        const newDates = dates.filter((_, i) => i !== index);
        onChange({
            ...value,
            dates: newDates,
        });
    };

    const handleClearAll = () => {
        onChange({
            ...value,
            dates: [],
        });
    };

    const handleSetAllDay = (checked) => {
        if (checked) {
            onChange({
                ...value,
                allDay: checked,
                startTime: [0, 0],
                endTime: [23, 59],
            });
        } else {
            onChange({
                ...value,
                allDay: checked,
                startTime: [9, 0],
                endTime: [9, 0],
            });
        }
    };

    const handleChangeStartTime = (time) => {
        onChange({
            ...value,
            startTime: timeStringToArray(time),
        });
    };

    const handleChangeEndTime = (time) => {
        onChange({
            ...value,
            endTime: timeStringToArray(time),
        });
    };

    return (
        <Wrapper>
            <div className="dates-row">
                <div className="date-picker-container">
                    <DatePicker
                        value={dates.map((d) => new DateObject(d))}
                        onChange={handleDatesChange}
                        multiple
                        format="DD/MM/YYYY"
                        sort
                        render={(pickerValue, openCalendar) => (
                            <NwIcon
                                className="add-date-icon"
                                icon={byPrefixAndName.fasl['calendar-circle-plus']}
                                onClick={openCalendar}
                            />
                        )}
                    />
                </div>
                <div className="selected-dates-tags">
                    {dates.length === 0 && (
                        <Alert message="No dates selected" type="error" style={{ marginTop: '1rem' }} />
                    )}
                    {dates.map((date, index) => (
                        <Tag
                            color={StyleVariables.PrimaryColor}
                            key={index}
                            closable
                            onClose={() => handleRemoveDate(index)}
                        >
                            {getMoment(date).format('DD/MM/YYYY')}
                        </Tag>
                    ))}

                    {dates.length > 1 && (
                        <span className="clear-all-btn" onClick={handleClearAll}>
                            <NwIcon icon={byPrefixAndName.fasl['times']} /> Clear all
                        </span>
                    )}
                </div>
            </div>
            <div className="time-row">
                <p>Time</p>
                <div className="all-day-container">
                    <Switch checked={allDay} onChange={handleSetAllDay} />
                    <span>all day</span>
                </div>
                {!allDay && (
                    <>
                        <TimePicker value={timeArrayToString(startTime)} onChange={handleChangeStartTime} />
                        <span>to</span>
                        <TimePicker value={timeArrayToString(endTime)} onChange={handleChangeEndTime} />
                    </>
                )}
            </div>
        </Wrapper>
    );
};

export default MultipleDateSelector;
